import { extendTheme } from '@chakra-ui/react';
import { ChatConfigWindow } from 'src';
import { Store } from '../store';

const scheme = {
  whiteCompatible: '#080808',
  backgroundPrimary: '#FFFFFF',
  backgroundInverted: '#080808',
  backgroundSecondary: '#F1F1F1',
  backgroundTransparent25: '#ffffff40',
  contentPrimary: '#080808',
  contentSecondary: '#555555',
  contentTertiary: '#767676',
  contentInverted: '#FFFFFF',
  contentInversePrimary: '#FFFFFF',
  contentInverseTransparent75: '#ffffffbf',
  contentInverseSecondary: '#C6C6C6',
  borderPrimary: '#DFDFDF',
  borderSecondary: '#A6A6A6',
  borderNegative: '#DD372B',
  borderTertiary: '#F1F1F1',
  activatorButtonPrimary: '#ED1941',
  neutral1: '#FCFCFC',
  neutral3: '#DFDFDF',
  purple0: '#F7EEFF',
  purple1: '#D8B4FB',
  purple2: '#BB7DF5',
  purple3: '#A04FEB',
  purple4: '#892BDD',
  purple5: '#7412CA',
  purple6: '#6102B0',
  purple7: '#510093',
  purple8: '#410074',
  purple9: '#310057'
};
const fontFamily = "'Inter', sans-serif";
const overrides = {
  styles: {
    global: {
      body: {
        bg: 'transparent'
      }
    }
  },
  colors: {
    scheme
  },
  fonts: {
    body: fontFamily
  },
  components: {
    Text: {
      baseStyle: {
        fontFamily
      },
      variants: {
        'paragraph-medium': {
          fontWeight: 400,
          fontSize: '14px',
          lineHeight: '130%',
          wordBreak: 'break-word',
          whiteSpace: 'pre-line',
          '& > a': {
            textDecoration: 'underline'
          }
        },
        'label-medium': {
          fontWeight: 500,
          fontSize: '12px',
          lineHeight: '130%'
        },
        'paragraph-small': {
          fontWeight: 400,
          fontSize: '12px',
          lineHeight: '130%',
          letterSpacing: '0.1px'
        },
        'label-small': {
          fontWeight: 500,
          fontSize: '12px',
          lineHeight: '125%',
          letterSpacing: '-0.1px'
        },
        'label-large': {
          fontWeight: 500,
          fontSize: '14px',
          lineHeight: '130%'
        },
        'heading-medium': {
          fontWeight: 600,
          fontSize: '18px',
          lineHeight: '120%'
        }
      }
    },
    Heading: {
      baseStyle: {
        fontFamily
      },
      variants: {
        'heading-large': {
          fontWeight: 600,
          fontSize: '24px',
          lineHeight: '150%',
          letterSpacing: '-0.8px'
        },
        'heading-medium': {
          fontWeight: 600,
          fontSize: '18px',
          lineHeight: '120%'
        }
      }
    },
    Textarea: {
      variants: {
        'main-chat': {
          fontFamily,
          fontWeight: 400,
          fontSize: '16px',
          lineHeight: '130%',
          backgroundColor: scheme.backgroundSecondary,
          color: scheme.contentPrimary,
          minHeight: '32px',
          paddingInlineStart: 0,
          '&::placeholder': {
            fontFamily: "'Inter', sans-serif",
            fontWeight: 400,
            fontSize: '16px',
            lineHeight: '130%',
            opacity: 1,
            color: scheme.contentTertiary
          },
          '::-webkit-scrollbar': {
            width: '2px'
          },
          '::-webkit-scrollbar-track': {
            background: scheme.backgroundSecondary
          },
          '::-webkit-scrollbar-thumb': {
            background: scheme.borderPrimary
          }
        },
        'in-chat-textarea': {
          fontFamily,
          fontWeight: 400,
          fontSize: '16px',
          lineHeight: '130%',
          backgroundColor: scheme.backgroundPrimary,
          color: scheme.contentPrimary,
          minHeight: '78px',
          borderRadius: '8px',
          padding: '12px',
          resize: 'none',
          border: `1px solid ${scheme.borderPrimary}`,
          _focus: {
            border: `1px solid ${scheme.borderSecondary}`
          },
          _active: {
            border: `1px solid ${scheme.borderSecondary}`
          },
          '::-webkit-scrollbar': {
            width: '2px'
          },
          '::-webkit-scrollbar-track': {
            background: scheme.backgroundSecondary
          },
          '::-webkit-scrollbar-thumb': {
            background: scheme.borderPrimary
          }
        }
      }
    },
    Button: {
      variants: {
        'chat-option': {
          height: '31.97px',
          borderRadius: '8px',
          padding: '8px 12px',
          border: `1px solid ${scheme.borderPrimary}`,
          '& > p': {
            letterSpacing: '-0.1px'
          },
          fontFamily,
          fontWeight: 500,
          fontSize: '12px',
          lineHeight: '130%',

          margin: '2px',
          color: scheme.contentSecondary,
          background: 'transparent',
          '&:hover': {
            border: `1px solid ${scheme.borderSecondary}`,
            color: scheme.contentPrimary
          },
          '&:active': {
            background: scheme.whiteCompatible,
            color: scheme.contentInversePrimary
          },
          '&:focus': {
            boxShadow: 'none'
          },
          _focusVisible: {
            border: `1px solid ${scheme.borderSecondary}`,
            color: scheme.contentPrimary
          }
        },
        'cta-chat-option': {
          height: '31.97px',
          borderRadius: '8px',
          padding: '8px 12px',
          '& > p': {
            letterSpacing: '-0.1px'
          },
          border: `1px solid ${scheme.whiteCompatible}`,
          fontFamily,
          fontWeight: 500,
          fontSize: '12px',
          lineHeight: '130%',
          margin: '2px',
          color: scheme.contentInversePrimary,
          background: scheme.whiteCompatible,
          _focus: { outline: 0, boxShadow: 'none' },
          _focusVisible: { outline: '1px solid', outlineColor: 'scheme.whiteCompatible' }
        },
        'close-activator-info-button': {
          height: '20px',
          width: '20px',
          minWidth: '20px',
          borderRadius: '8px',
          padding: '2px',
          border: `1px solid ${scheme.borderPrimary}`,
          color: scheme.contentSecondary,
          background: scheme.backgroundPrimary,
          sx: {
            '& > svg': {
              stroke: 'scheme.contentTertiary',
              strokeWidth: '2px'
            }
          },
          _hover: {
            '& > svg': {
              stroke: scheme.contentPrimary
            }
          },
          _active: {
            '& > svg': {
              stroke: scheme.contentInversePrimary
            }
          },
          _focus: {
            boxShadow: 'none'
          }
        },
        'selected-nps-option': {
          height: '32px',
          borderRadius: '8px',
          padding: '8px 12px',
          fontFamily,
          fontWeight: 500,
          fontSize: '12px',
          lineHeight: '130%',
          margin: '2px',
          border: `1px solid ${scheme.whiteCompatible}`,
          background: scheme.whiteCompatible,
          color: scheme.contentInversePrimary,
          _focus: { outline: 0, boxShadow: 'none' },
          _focusVisible: { outline: '1px solid', outlineColor: 'scheme.whiteCompatible' }
        },
        'selected-chat-option': {
          height: '31.97px',
          transition: 'all 0.3s ease-in-out',
          transform: 'scale(1, 1.2713)',
          borderRadius: '8px',
          padding: '12px',
          fontFamily,
          fontWeight: 400,
          fontSize: '11px',
          lineHeight: '130%',
          margin: '2px',
          border: 0,
          outline: 0,
          background: scheme.whiteCompatible,
          color: scheme.contentInversePrimary,
          _focus: { outline: 0, boxShadow: 'none' },
          _focusVisible: { outline: '1px solid', outlineColor: 'scheme.whiteCompatible' }
        },
        'submit-cta': {
          borderRadius: '8px',
          height: '32px',
          padding: '8px 12px',
          fontFamily,
          fontSize: '12px',
          lineHeight: '130%',
          fontWeight: 500,
          width: '100%',
          color: scheme.contentInversePrimary,
          background: scheme.whiteCompatible,
          '&:disabled': {
            background: 'transparent',
            border: `1px solid ${scheme.borderPrimary}`,
            color: scheme.contentSecondary
          },
          _focus: { outline: 0, boxShadow: 'none' },
          _focusVisible: {
            _after: {
              content: '""',
              display: 'block',
              position: 'absolute',
              top: '-3px',
              bottom: '-3px',
              left: '-3px',
              right: '-3px',
              borderRadius: '8px',
              padding: '10px',
              border: `1px solid ${scheme.whiteCompatible}`,
              pointerEvents: 'none'
            }
          }
        },
        'back-button': {
          height: '32px',
          background: scheme.whiteCompatible,
          borderRadius: '8px',
          fontFamily,
          fontWeight: 500,
          fontSize: '12px',
          lineHeight: '140%',
          color: scheme.contentInversePrimary
        }
      }
    },
    Input: {
      variants: {
        'in-chat-input': {
          field: {
            borderRadius: '8px',
            height: '42px',
            color: scheme.contentPrimary,
            '&::placeholder': {
              fontFamily: "'Inter', sans-serif",
              fontWeight: 400,
              fontSize: '14px',
              opacity: 1,
              color: scheme.contentTertiary
            },
            border: `1px solid ${scheme.borderPrimary}`,
            '&:hover': {
              boxShadow: '0px 2px 4px rgba(0, 0, 0, 0.06)'
            },
            '&:focus': {
              boxShadow: '0px 2px 4px rgba(0, 0, 0, 0.06)',
              border: `1px solid ${scheme.borderSecondary}`
            },
            _invalid: {
              border: `1px solid ${scheme.borderNegative}`
            }
          }
        }
      }
    },
    Menu: {
      variants: {
        'chat-header': {
          list: {
            padding: '4px',
            borderRadius: '8px',
            background: scheme.backgroundPrimary,
            width: '200px',
            minWidth: 0,
            zIndex: 'modal'
          },
          container: {
            maxWidth: '200px'
          },
          item: {
            fontFamily,
            color: scheme.contentSecondary,
            borderRadius: '6px',
            _hover: {
              background: scheme.backgroundSecondary,
              color: scheme.contentPrimary
            },
            _focus: {
              background: scheme.backgroundSecondary,
              color: scheme.contentPrimary
            }
          }
        },
        'ai-persona-indicator': {
          list: {
            border: '1px solid',
            borderColor: scheme.borderPrimary,
            borderRadius: '8px',
            background: scheme.backgroundPrimary,
            width: '200px',
            minWidth: 0,
            padding: '4px'
          },
          item: {
            fontFamily,
            color: scheme.contentSecondary,
            borderRadius: '6px',
            _hover: {
              background: scheme.backgroundSecondary,
              color: scheme.contentPrimary
            },
            _focus: {
              background: scheme.backgroundSecondary,
              color: scheme.contentPrimary
            }
          }
        }
      }
    },
    Tooltip: {
      baseStyle: {
        background: scheme.backgroundInverted
      },
      variants: {
        'invalid-input': {
          background: scheme.backgroundInverted,
          borderRadius: '8px',
          color: scheme.contentInversePrimary,
          fontFamily,
          fontSize: '12px',
          fontWeight: 400,
          lineHeight: '130%',
          letterSpacing: '0.1px',
          padding: '4px 8px',
          '--tooltip-bg': scheme.backgroundInverted,
          filter: 'drop-shadow(0px 2px 4px rgba(0, 0, 0, 0.06))',
          arrow: {
            background: scheme.backgroundInverted
          }
        }
      }
    }
  }
};

declare let window: ChatConfigWindow;

export type Overrides = typeof overrides;
export default extendTheme({ ...overrides, ...Store.getRawState().config });
